import React from 'react';
import { Box, TextField, IconButton, Typography, MenuItem } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';

interface StockEntry {
  stock_type: 'in_store' | 'online';
  stock: number;
}

interface StockLocation {
  name: string;
  stocks: StockEntry[];
}

interface StockInputProps {
  locations: StockLocation[];
  allLocations: { name: string }[];
  onLocationsChange: (updatedLocations: StockLocation[]) => void;
}

const StockInput: React.FC<StockInputProps> = ({ locations, allLocations, onLocationsChange }) => {
  const handleAddLocation = () => {
    onLocationsChange([
      ...locations,
      {
        name: '',
        stocks: [
          { stock_type: 'in_store', stock: 0 },
          { stock_type: 'online', stock: 0 },
        ],
      },
    ]);
  };

  const handleLocationChange = (
    index: number,
    field: 'name' | 'stock',
    value: string | number,
    stockType?: 'in_store' | 'online'
  ) => {
    const updated = [...locations];
    if (field === 'name') {
      updated[index].name = value as string;
    } else if (stockType) {
      const numeric = Number(value);
      updated[index].stocks = updated[index].stocks.map((s) =>
        s.stock_type === stockType ? { ...s, stock: isNaN(numeric) ? 0 : numeric } : s
      );
    }
    onLocationsChange(updated);
  };

  const handleDeleteLocation = (index: number) => {
    onLocationsChange(locations.filter((_, i) => i !== index));
  };

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        Stock by Location & Type
      </Typography>
      {locations.map((location, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            mb: 2,
            border: '1px solid #ddd',
            p: 2,
            borderRadius: 1,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              select
              label="Location Name"
              value={location.name || ''}
              onChange={(e) => handleLocationChange(index, 'name', e.target.value)}
              fullWidth
              size="small"
            >
              {allLocations.map((availableLocation, idx) => (
                <MenuItem key={idx} value={availableLocation.name}>
                  {availableLocation.name}
                </MenuItem>
              ))}
            </TextField>
            <IconButton color="error" onClick={() => handleDeleteLocation(index)}>
              <Delete />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {(['in_store', 'online'] as const).map((type) => {
              const stockVal = location.stocks.find((s) => s.stock_type === type)?.stock ?? 0;
              return (
                <TextField
                  key={type}
                  label={type === 'in_store' ? 'In-Store Stock' : 'Online Stock'}
                  type="number"
                  value={stockVal === 0 ? '' : stockVal}
                  onChange={(e) =>
                    handleLocationChange(index, 'stock', e.target.value, type)
                  }
                  fullWidth
                  size="small"
                  inputProps={{ inputMode: 'numeric' }}
                />
              );
            })}
          </Box>
        </Box>
      ))}
      <IconButton color="primary" onClick={handleAddLocation}>
        <Add />
      </IconButton>
    </Box>
  );
};

export default StockInput;
